import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";
import { Mail, KeyRound, Loader2, ArrowRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const LoginPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState("email"); // "email" or "code"
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [testCode, setTestCode] = useState(null);

  const handleRequestCode = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/auth/request-code`, { email });
      setStep("code");
      toast.success(res.data.message);
      if (res.data.test_code) {
        setTestCode(res.data.test_code);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Noget gik galt");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/auth/verify-code`, { email, code });
      localStorage.setItem("mf_token", res.data.token);
      localStorage.setItem("mf_email", res.data.email);
      toast.success("Du er logget ind!");
      navigate("/mine-taler");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Forkert kode");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center py-16 px-4">
      <Card className="w-full max-w-md border-border/40" data-testid="login-card">
        <CardContent className="p-8">
          {step === "email" ? (
            <>
              <div className="text-center mb-6">
                <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="h-7 w-7 text-primary" />
                </div>
                <h1 className="font-heading text-2xl font-bold text-primary">
                  Log ind
                </h1>
                <p className="text-muted-foreground mt-2">
                  Indtast den email du brugte ved køb, så sender vi en kode.
                </p>
              </div>
              <form onSubmit={handleRequestCode} className="space-y-4">
                <Input
                  type="email"
                  placeholder="din@email.dk"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-12 rounded-lg"
                  data-testid="login-email-input"
                />
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-primary hover:bg-primary/90 rounded-lg font-medium"
                  data-testid="login-send-code-btn"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>Send kode <ArrowRight className="ml-2 h-4 w-4" /></>
                  )}
                </Button>
              </form>
            </>
          ) : (
            <>
              <div className="text-center mb-6">
                <div className="w-14 h-14 bg-secondary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <KeyRound className="h-7 w-7 text-secondary" />
                </div>
                <h1 className="font-heading text-2xl font-bold text-primary">
                  Indtast kode
                </h1>
                <p className="text-muted-foreground mt-2">
                  Vi har sendt en 6-cifret kode til <strong>{email}</strong>
                </p>
              </div>

              {testCode && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-center" data-testid="test-code-display">
                  <p className="text-xs text-amber-600 mb-1">Test-tilstand — koden er:</p>
                  <p className="text-2xl font-bold text-amber-800 tracking-widest">{testCode}</p>
                </div>
              )}

              <form onSubmit={handleVerifyCode} className="space-y-4">
                <Input
                  type="text"
                  placeholder="123456"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  required
                  maxLength={6}
                  className="h-14 rounded-lg text-center text-2xl tracking-[0.5em] font-mono"
                  data-testid="login-code-input"
                />
                <Button
                  type="submit"
                  disabled={loading || code.length !== 6}
                  className="w-full h-12 bg-primary hover:bg-primary/90 rounded-lg font-medium"
                  data-testid="login-verify-btn"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    "Log ind"
                  )}
                </Button>
                <button
                  type="button"
                  onClick={() => { setStep("email"); setCode(""); setTestCode(null); }}
                  className="w-full text-sm text-muted-foreground hover:text-primary transition-colors"
                  data-testid="login-back-btn"
                >
                  Brug en anden email
                </button>
              </form>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;
